import { FC, useEffect, useState } from 'react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';
import { useHotelsListApi } from '@/features/hotels/hooks/useHotelsListApi';
import { useHotelStorage } from '@/features/hotels/hooks/useHotelStorage';
import { GetHotelListItemDto } from '@/features/hotels/types/hotels-dto';
import { ColorsHelper } from '@/utils/ColorsHelper';
import { useMultiCurrencyStorage } from '../hooks/useHotelMultiCurrencyStorage';

type Props = {};

const SelectHotel: FC<Props> = () => {
	const [isOpen, setIsOpen] = useState(false);

	const { hotelsListData, doRefresh } = useHotelsListApi();

	const { selectedHotel, setSelectedHotel } = useHotelStorage();
	const { setMultiCurrency, setOrdersEnabled, setCurrencies } = useMultiCurrencyStorage();

	// Only default to the first hotel when the list loads or the current selection is no longer valid.
	// Previously this effect also depended on selectedHotel and always set items[0], which overwrote any user pick.
	useEffect(() => {
		if (!hotelsListData?.items?.length) return;
		const items = hotelsListData.items;
		const selectedId = selectedHotel?.id;
		const isStillInList = Boolean(selectedId && items.some((h) => h.id === selectedId));
		if (!isStillInList) {
			setSelectedHotel(items[0]);
		}
	}, [hotelsListData, selectedHotel, setSelectedHotel]);

	useEffect(() => {
		setMultiCurrency(hotelsListData?.multiCurrency || false);
		setOrdersEnabled(hotelsListData?.ordersEnabled ?? true);
		setCurrencies({
			calendarCurrency: hotelsListData?.calendarCurrency,
			financePropertyCurrency: hotelsListData?.financePropertyCurrency,
			financeServicesCurrency: hotelsListData?.financeServicesCurrency,
			financeOtherCurrency: hotelsListData?.financeOtherCurrency,
		});

		const setColor = document.documentElement.style.getPropertyValue('--color-primary-color');

		const setDarkMode =
			document.documentElement.style.getPropertyValue('--color-primary-contarst') === 'black';

		if (
			hotelsListData?.primaryColor &&
			(hotelsListData.primaryColor !== setColor || hotelsListData.darkMode !== setDarkMode)
		) {
			ColorsHelper.setNewColor(hotelsListData.primaryColor, hotelsListData.darkMode);
		}
	}, [hotelsListData, setMultiCurrency, setOrdersEnabled, setCurrencies]);

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
	};

	const handleSelectHotel = async (hotel: GetHotelListItemDto) => {
		setSelectedHotel(hotel);
		setIsOpen(false);
		await doRefresh();
	};

	return (
		<DropdownMenu
			open={isOpen}
			onOpenChange={handleOpenChange}
			modal={true}>
			<DropdownMenuTrigger
				asChild
				className={'px-4 p-3 rounded-semi-xl'}>
				<Button
					variant='outline'
					className={cn('max-w-60 gap-2.5')}>
					<div className='size-5 shrink-0 bg-slate-500 rounded-full' />
					<span className='truncate'>{selectedHotel?.name}</span>
					<div
						className={`ml-auto transition-transform duration-50 ease-in-out ${
							isOpen ? '-rotate-180' : 'rotate-0'
						}`}>
						{<ArrowDownIcon />}
					</div>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				className={
					'w-(--radix-dropdown-menu-trigger-width) max-h-56 h-fit p-4 flex flex-col gap-2 rounded-2.5xl border border-popover-100 bg-popover-10 shadow-custom overflow-y-scroll custom-scrollbar'
				}>
				<div className='flex flex-col gap-2'>
					{hotelsListData?.items?.map((hotel) => (
						<button
							key={hotel.id}
							className='px-3 py-2.5 flex items-center gap-2.5 rounded-semi-xl text-start hover:bg-primary-color-50 active:bg-primary-color-100'
							onClick={() => handleSelectHotel(hotel)}>
							<div className='size-5 shrink-0 bg-slate-500 rounded-full' />
							<span className='truncate'>{hotel.name}</span>
							<div
								className={cn(
									'size-4 ml-auto shrink-0 border-primary-color-1000 rounded-full transition-all',
									hotel.id === selectedHotel?.id
										? 'border-4'
										: 'border',
								)}
							/>
						</button>
					))}
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default SelectHotel;
